import { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTruck, faArrowLeft, faMapMarkerAlt, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel,
} from "@mui/material";
import { getCategoryColor } from "../../constants/materialCategories";
import "../../style/MapListingsTable.css";

const ROWS_PER_PAGE = 10;

const fmtDate = (str) => {
  if (!str) return "—";
  try {
    const [y, m, d] = String(str).split(/[-T]/);
    return new Date(Number(y), Number(m) - 1, Number(d))
      .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return String(str).slice(0, 10); }
};

function sortVal(listing, key) {
  switch (key) {
    case "companyName": return (listing.companyName || "").toLowerCase();
    case "material":    return (listing.material || listing.item_name || "").toLowerCase();
    case "quantity":    return Number(listing.remaining_qty ?? listing.quantity ?? 0);
    case "cost":        return Number(listing.cost ?? 0);
    case "delivery":    return (listing.delivery_method || "pickup").toLowerCase();
    case "deadline":    return listing.latest_pickup || "9999";
    default:            return "";
  }
}

function MapListingsTable({
  listings,
  selectedCompany,
  onClearSelection,
  onSelectListing,
  darkMode,
}) {
  const { t } = useTranslation();
  const [orderBy, setOrderBy] = useState("material");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);

  // reset page when data changes
  const listingsKey = listings?.length ?? 0;
  const prevKey = useMemo(() => ({ v: listingsKey }), [listingsKey]);
  useMemo(() => { setPage(0); }, [prevKey]); // eslint-disable-line

  const handleSort = useCallback((key) => {
    setOrder((prev) => orderBy === key && prev === "asc" ? "desc" : "asc");
    setOrderBy(key);
    setPage(0);
  }, [orderBy]);

  const sorted = useMemo(() => {
    if (!listings?.length) return [];
    return [...listings].sort((a, b) => {
      const va = sortVal(a, orderBy);
      const vb = sortVal(b, orderBy);
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return order === "asc" ? cmp : -cmp;
    });
  }, [listings, orderBy, order]);

  const totalPages = Math.ceil(sorted.length / ROWS_PER_PAGE);
  const paginated = useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return sorted.slice(start, start + ROWS_PER_PAGE);
  }, [sorted, page]);

  const isSelected = !!selectedCompany;
  const count = listings?.length || 0;

  const mkTh = (label, key, align = "left") => (
    <TableCell className="mm-table__th" align={align} sortDirection={orderBy === key ? order : false}>
      <TableSortLabel active={orderBy === key} direction={orderBy === key ? order : "asc"}
        onClick={() => handleSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <div className={`mlt ${darkMode ? "mlt--dark" : "mlt--light"}`}>
      <div className="mlt__header">
        {isSelected ? (
          <div className="mlt__sel">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="mlt__sel-icon" />
            <span className="mlt__sel-name">
              {selectedCompany.company_name || selectedCompany.companyName || "—"}
              <span className="mlt__sel-count"> ({count})</span>
            </span>
            <button className="mlt__clear" onClick={onClearSelection}>
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{t("Back to all")}</span>
            </button>
          </div>
        ) : (
          <span className="mlt__total">
            {t("Listings")}
            <span className="mlt__total-count"> ({count})</span>
          </span>
        )}
      </div>

      <Box className="mm-table">
        <Box className="mm-table__wrap">
          <Table size="small" className="mm-table__table">
            <TableHead>
              <TableRow className="mm-table__tr mm-table__tr--head">
                {!isSelected && mkTh(t("Company"), "companyName")}
                {mkTh(t("Material"), "material")}
                {mkTh(t("Qty (lbs)"), "quantity", "center")}
                {mkTh(t("Cost/lb"), "cost", "center")}
                {mkTh(t("Method"), "delivery", "center")}
                {mkTh(t("Deadline"), "deadline", "center")}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell className="mm-table__td" colSpan={isSelected ? 5 : 6} align="center" sx={{ py: 3 }}>
                    {t("No listings found")}
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((l) => {
                  const isFree = Number(l.cost) === 0;
                  const qty = l.remaining_qty ?? l.quantity ?? 0;
                  const method = String(l.delivery_method || "pickup").toLowerCase();
                  const isDelivery = method === "delivery";

                  return (
                    <TableRow key={l.id} hover className="mm-table__tr mm-table__tr--body"
                      onClick={() => onSelectListing?.(l)} sx={{ cursor: "pointer" }}>
                      {!isSelected && (
                        <TableCell className="mm-table__td mm-table__td--semi">{l.companyName || "—"}</TableCell>
                      )}
                      <TableCell className="mm-table__td mm-table__td--strong">
                        <span style={{
                          display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                          marginRight: 8, verticalAlign: "middle",
                          background: getCategoryColor(l.material_category),
                        }} />
                        {l.item_name || l.material || "—"}
                      </TableCell>
                      <TableCell className="mm-table__td" align="center">
                        {Number(qty).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell className={`mm-table__td${isFree ? " mm-table__td--strong" : ""}`} align="center"
                        sx={isFree ? { color: "#7BC043 !important" } : undefined}>
                        {isFree ? t("Free") : `$${Number(l.cost).toFixed(2)}`}
                      </TableCell>
                      <TableCell className="mm-table__td" align="center">
                        {isDelivery ? (
                          <span className="mlt__delivery-badge">
                            <FontAwesomeIcon icon={faTruck} /> {t("Delivery")}
                          </span>
                        ) : (
                          <span className="mlt__pickup-badge">
                            <FontAwesomeIcon icon={faBoxOpen} /> {t("Pickup")}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="mm-table__td" align="center">
                        {fmtDate(l.latest_pickup)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {sorted.length > ROWS_PER_PAGE && (
        <div className="mm-pagination" style={{ padding: "12px 16px" }}>
          <span className="mm-pagination__count">
            {t("Showing {{start}}–{{end}} of {{total}}", {
              start: page * ROWS_PER_PAGE + 1,
              end: Math.min((page + 1) * ROWS_PER_PAGE, sorted.length),
              total: sorted.length,
            })}
          </span>
          <div className="mm-pagination__controls">
            <button className="mm-pagination__btn"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}>
              ‹
            </button>
            <span className="mm-pagination__info">
              {page + 1} / {totalPages}
            </span>
            <button className="mm-pagination__btn"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}>
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

MapListingsTable.propTypes = {
  listings: PropTypes.array,
  selectedCompany: PropTypes.object,
  onClearSelection: PropTypes.func,
  onSelectListing: PropTypes.func,
  darkMode: PropTypes.bool,
};

MapListingsTable.defaultProps = { listings: [] };

export default MapListingsTable;
